import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Shield, Scale, ArrowRight } from "lucide-react";

const reports = [
  {
    id: "task-force-brief",
    path: "/reports/task-force-brief",
    title: "Closing the Verification Gap",
    subtitle: "A Structural Solution to Pharmaceutical Fraud Across Medicare and Medicaid",
    description: "Policy analysis for the Task Force to Eliminate Fraud. Addresses $94B+ in annual improper payments, the structural gap between DSCSA serialization and claims adjudication, and a zero-cost pilot recommendation.",
    pages: 16,
    date: "March 2026",
    icon: Scale,
    accent: "#b91c1c",
    tag: "FEDERAL POLICY ANALYSIS"
  },
  {
    id: "state-briefing",
    path: "/reports/state-briefing",
    title: "MENDRx State Policy Briefing",
    subtitle: "State of Michigan — Serialized Reimbursement Infrastructure",
    description: "State-level policy briefing covering Michigan's Medicaid and Medicare landscape ($25.4B Medicaid, 2.25M Medicare beneficiaries), pharmacy fraud patterns, the DSCSA serialization opportunity, and a proposed 90-day pilot program.",
    pages: 14,
    date: "March 2026",
    icon: FileText,
    accent: "#1e40af",
    tag: "STATE BRIEFING"
  },
  {
    id: "fwa-case-analysis",
    path: "/reports/fwa-case-analysis",
    title: "Michigan Healthcare Fraud Case Analysis",
    subtitle: "How Serialized Reimbursement Verification Prevents Each Scheme",
    description: "Comprehensive analysis of 12 documented healthcare fraud cases in Michigan (2022-2026). 8 pharmacy cases ($49.3M+ confirmed) directly addressable by serialized reimbursement verification. Includes pattern analysis and prevention mechanisms.",
    pages: 21,
    date: "March 2026",
    icon: Shield,
    accent: "#0d9488",
    tag: "CASE ANALYSIS"
  }
];

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

export default function Dashboard() {
  const navigate = useNavigate();

  return (
    <div className="dashboard-container" data-testid="dashboard">
      <div className="dashboard-bg"></div>
      
      <motion.header 
        className="dashboard-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="dashboard-brand">
          <span className="brand-logo">MENDRX</span>
          <span className="brand-divider"></span>
          <span className="brand-sub">Strategic Report Hub</span>
        </div>
        <div className="dashboard-classification">CONFIDENTIAL</div>
      </motion.header>

      <motion.div 
        className="dashboard-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <h1 className="dashboard-title">Serialized Reimbursement<br />Intelligence Reports</h1>
        <div className="dashboard-state-badge" data-testid="state-badge">STATE OF MICHIGAN</div>
        <p className="dashboard-desc">
          Comprehensive policy analyses and case studies demonstrating how serialized reimbursement 
          verification closes the pharmaceutical fraud gap across Medicare and Medicaid.
        </p>
        <div className="dashboard-stats-row">
          <div className="dash-stat">
            <span className="dash-stat-num">$94B+</span>
            <span className="dash-stat-label">Annual Improper Payments</span>
          </div>
          <div className="dash-stat">
            <span className="dash-stat-num">41B+</span>
            <span className="dash-stat-label">Serialized Products Tracked</span>
          </div>
          <div className="dash-stat">
            <span className="dash-stat-num">3</span>
            <span className="dash-stat-label">Strategic Reports</span>
          </div>
        </div>
      </motion.div>

      <motion.div 
        className="dashboard-grid"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >
        {reports.map((report) => {
          const Icon = report.icon;
          return (
            <motion.div
              key={report.id}
              className="report-card"
              variants={fadeUp}
              whileHover={{ y: -4, boxShadow: "0 12px 40px rgba(0,0,0,0.15)" }}
              onClick={() => navigate(report.path)}
              data-testid={`report-card-${report.id}`}
              style={{ cursor: "pointer" }}
            >
              <div className="card-accent" style={{ background: report.accent }}></div>
              <div className="card-content">
                <div className="card-top">
                  <span className="card-tag" style={{ color: report.accent, borderColor: report.accent }}>
                    {report.tag}
                  </span>
                  <span className="card-date">{report.date}</span>
                </div>
                <div className="card-icon-wrap" style={{ background: `${report.accent}12` }}>
                  <Icon size={24} color={report.accent} />
                </div>
                <h2 className="card-title">{report.title}</h2>
                <p className="card-subtitle">{report.subtitle}</p>
                <p className="card-desc">{report.description}</p>
                <div className="card-footer">
                  <span className="card-pages">{report.pages} pages</span>
                  <span className="card-cta" style={{ color: report.accent }}>
                    View Report <ArrowRight size={16} />
                  </span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      <footer className="dashboard-footer">
        <p>Prepared by MENDRx | All figures verified against primary federal sources</p>
        <p className="footer-contacts">
          Steve Madsen, Co-Founder — Steve@MendRx.Ai | Tony Madsen, Co-Founder — Tony@MendRx.Ai
        </p>
      </footer>
    </div>
  );
}
